import { useState } from "react";
import { Moon, Sun, Sunset } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useExperience } from "@/context/ExperienceContext";
import { SCENES, formatHour } from "@/data/scenes";
import { cn } from "@/lib/utils";

const PHASE = {
  morning: { icon: Sun, color: "#FACC15" },
  afternoon: { icon: Sun, color: "#38BDF8" },
  sunset: { icon: Sunset, color: "#FB923C" },
  night: { icon: Moon, color: "#1E3A8A" },
  dawn: { icon: Sun, color: "#FACC15" },
};

// The logo's own alarm-clock emblem, with a live hand over its face (face centre ≈ 43% / 52%).
function EmblemClock({ hour, phase }) {
  const angle = (hour / 24) * 360;
  const { icon: Icon, color } = PHASE[phase];
  return (
    <span className="rrb-emblem-clock">
      <img src="/media/rrb-emblem-sm.png" alt="" draggable="false" />
      <svg viewBox="0 0 100 100" className="rrb-emblem-hand" aria-hidden>
        <g style={{ transform: `rotate(${angle}deg)`, transformOrigin: "50px 50px", transition: "transform .3s linear" }}>
          <line x1="50" y1="56" x2="50" y2="14" stroke="#fff" strokeWidth="9" strokeLinecap="round" opacity=".9" />
          <line x1="50" y1="56" x2="50" y2="14" stroke="#EC4899" strokeWidth="5" strokeLinecap="round" />
        </g>
        <circle cx="50" cy="50" r="7" fill="#FACC15" stroke="#1E3A8A" strokeWidth="2.5" />
      </svg>
      <span className="rrb-emblem-phase" style={{ background: color }}>
        <Icon className="h-2.5 w-2.5" strokeWidth={3} color={phase === "night" ? "#FDE68A" : "#1B2559"} />
      </span>
    </span>
  );
}

export function Clock24({ className }) {
  const { hour, phase, sceneIndex, jumpTo, finale } = useExperience();
  const [openMenu, setOpenMenu] = useState(false);
  return (
    <Popover open={openMenu} onOpenChange={setOpenMenu}>
      <PopoverTrigger asChild>
        <button data-testid="clock-nav-widget" aria-label="Open scene menu" className={cn("rrb-clock group", className)}>
          <EmblemClock hour={hour} phase={phase} />
          <span className="rrb-clock-text">
            <span data-testid="clock-time-label" className="font-display font-semibold tabular-nums">{formatHour(hour)}</span>
            <span className="rrb-clock-scene">{finale > 0.35 ? "Enroll" : SCENES[sceneIndex].label}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} data-testid="clock-scene-menu" className="rrb-menu w-64 p-2">
        <p className="px-3 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[0.2em] opacity-60">Jump through the day</p>
        {SCENES.map((s, i) => (
          <button key={s.id} data-testid={`clock-scene-${s.id}`}
            onClick={() => { jumpTo(i); setOpenMenu(false); }}
            className={cn("rrb-menu-item", i === sceneIndex && finale <= 0.35 && "is-active")}>
            <span className="font-display">{s.label}</span>
            <span className="text-xs tabular-nums opacity-60">{formatHour(s.hour)}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
